import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Outlet, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  LayoutDashboard, 
  Trophy, 
  Gauge, 
  Car, 
  Settings, 
  HelpCircle, 
  LogOut, 
  Bell, 
  Flag,
  ChevronDown,
  Users,
  CalendarDays,
  Menu,
  X,
  ShieldAlert,
  Scale,
  Home,
  TrendingUp,
  History,
  MessageSquare,
  BarChart2,
  ShoppingBag,
  Mic2,
  Bot
} from 'lucide-react';
import LanguageSelector from '../LanguageSelector';
import { useGame, GAMES } from '../../context/GameContext';
import { useAuth } from '../../context/AuthContext';
import { IconButton } from '../ui/IconButton';
import { NotificationDropdown } from '../ui/NotificationDropdown';
import { WalletDropdown } from '../ui/WalletDropdown';
import { incidentsApi, usersApi } from '../../lib/api';
import CountryCheckModal from '../CountryCheckModal';
import PrivacyConsentModal from '../auth/PrivacyConsentModal';
import { DefenseModal } from '../event/DefenseModal';
import { InstallPrompt } from '../pwa/InstallPrompt';
import PromotionModal from '../PromotionModal';
import MarblesGrantModal from '../MarblesGrantModal';
import { useTierPromotion } from '../../hooks/useTierPromotion';
import { useMarblesGrant } from '../../hooks/useMarblesGrant';
import { useActiveMrciCycle } from '../../hooks/useActiveMrciCycle';

const NavItem = ({ icon, label, active = false, activeColor = '#98C136', onClick, isCollapsed = false, badgeCount = 0 }: { icon: any, label: string, active?: boolean, activeColor?: string, onClick?: () => void, isCollapsed?: boolean, badgeCount?: number }) => (
  <button 
    onClick={onClick}
    title={isCollapsed ? label : ''}
    className={`flex items-center gap-3 px-4 py-3 rounded-lg font-label text-xs tracking-widest uppercase transition-all duration-300 w-full text-left ${
      active ? 'shadow-inner scale-[0.98] font-bold' : 'text-console-muted hover:bg-[var(--console-text)]/5 hover:text-console-text'
    } ${isCollapsed ? 'justify-center px-0' : ''}`}
    style={active ? { 
      backgroundColor: `${activeColor}1a`, 
      color: activeColor,
      borderLeft: isCollapsed ? 'none' : `4px solid ${activeColor}`,
      borderRight: isCollapsed ? `4px solid ${activeColor}` : 'none'
    } : {}}
  >
    <div className={`relative transition-transform duration-300 ${isCollapsed ? 'scale-110' : ''}`}>
      {icon}
      {badgeCount > 0 && (
        <span className="absolute -top-2 -right-2 flex h-4 w-4 items-center justify-center rounded-full bg-[#dc2937] text-[9px] font-black text-white shadow-lg ring-2 ring-console-surface animate-in zoom-in duration-300">
          {badgeCount}
        </span>
      )}
    </div>
    {!isCollapsed && <span className="truncate">{label}</span>}
  </button>
);

const Sidebar = ({ isOpen, onClose, isCollapsed }: { isOpen: boolean, onClose: () => void, isCollapsed: boolean }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { selectedGame } = useGame();
  const { user, emssUser, signOut } = useAuth();
  
  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };
  
  const [pendingStewardshipCount, setPendingStewardshipCount] = useState<number>(0);

  useEffect(() => {
    // Only fetch if user is a steward
    if (emssUser?.roles?.includes('steward')) {
      const fetchCount = async () => {
        try {
          const list = await incidentsApi.myAssignments();
          const pending = list?.filter((i: any) => i.status === 'open' || i.status === 'review').length || 0;
          setPendingStewardshipCount(pending);
        } catch (err) {
          console.error('Erro ao buscar contagem de stewardship:', err);
        }
      };
      
      fetchCount();
      
      // Refresh a cada 5 minutos
      const interval = setInterval(fetchCount, 5 * 60 * 1000);
      return () => clearInterval(interval);
    } else {
      setPendingStewardshipCount(0);
    }
  }, [emssUser?.id, emssUser?.roles?.join(',')]);

  const [unreadMsgCount, setUnreadMsgCount] = useState<number>(0);

  useEffect(() => {
    if (!emssUser?.id) return;
    let cancelled = false;
    let activeChannel: any = null;

    const fetchUnread = async () => {
      try {
        const { messagesApi } = await import('../../lib/api');
        const convs = await messagesApi.getConversations();
        const total = (convs || []).reduce((sum: number, c: any) => sum + (c.unreadCount ?? 0), 0);
        if (!cancelled) setUnreadMsgCount(total);
      } catch { /* silently ignore */ }
    };

    const setupRealtime = async () => {
      try {
        const { supabase } = await import('../../lib/supabase');
        const channelName = `user_events:${emssUser.id}`;
        activeChannel = supabase
          .channel(channelName, {
            config: {
              broadcast: { self: false }
            }
          })
          .on('broadcast', { event: 'new_message_alert' }, () => {
            fetchUnread();
          })
          .subscribe();
      } catch { /* silently ignore */ }
    };

    fetchUnread();
    setupRealtime();

    // Fallback de 5 min — o gatilho primário de atualização é o canal
    // realtime `new_message_alert` (broadcast). Antes era 30s + broadcast,
    // gerando ~2 requests/duplicação por usuário logado sem necessidade.
    const interval = setInterval(fetchUnread, 5 * 60_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
      if (activeChannel) {
        import('../../lib/supabase').then(({ supabase }) => {
          supabase.removeChannel(activeChannel);
        });
      }
    };
  }, [emssUser?.id]);

  const userName = emssUser?.display_name || user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email?.split('@')[0] || 'PILOTO_01';

  // Preset avatar mapping (deve espelhar PRESET_AVATARS em SettingsPage)
  const PRESET_AVATAR_SRCS: Record<string, string | null> = {
    preset_m1: '/assets/avatar_m1.png',
    preset_m2: '/assets/avatar_m2.png',
    preset_m3: '/assets/avatar_m3.png',
    preset_m4: '/assets/avatar_m4.png',
    preset_f1: '/assets/avatar_f1.png',
    preset_f2: '/assets/avatar_f2.png',
  };
  const savedAvatarId = emssUser?.id ? localStorage.getItem(`emss_avatar_id_${emssUser.id}`) : null;
  const userAvatar = (() => {
    if (savedAvatarId && savedAvatarId !== 'google' && PRESET_AVATAR_SRCS[savedAvatarId]) {
      return PRESET_AVATAR_SRCS[savedAvatarId] as string;
    }
    return emssUser?.avatar_url || user?.user_metadata?.avatar_url || '/assets/default-avatar.png';
  })();
  
  return (
    <>
      <div 
        className={`fixed inset-0 bg-console-bg/60 backdrop-blur-sm z-40 md:hidden transition-opacity duration-300 ${isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
        onClick={onClose}
      />
      
      <aside className={`fixed left-0 top-0 h-full ${isCollapsed ? 'w-[80px]' : 'w-[220px]'} rounded-r-lg bg-console-surface flex flex-col py-8 px-4 shadow-[4px_0_12px_rgba(0,0,0,0.1)] z-50 transform transition-all duration-300 md:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'} `}>
        <div className={`flex ${isCollapsed ? 'flex-col items-center' : 'justify-between items-start'} mb-10 px-2 cursor-pointer group`}>
          <div onClick={() => handleNavigation(user ? (selectedGame.slug === 'forza-horizon-6' ? '/fh6/profile' : '/profile') : '/login')} className={isCollapsed ? 'flex flex-col items-center' : ''}>
            <div className={`${isCollapsed ? 'w-10 h-10' : 'w-12 h-12'} rounded-full ${!isCollapsed ? 'mb-4' : ''} neo-out overflow-hidden border-2 border-console-border group-hover:border-game-primary/50 transition-all`}>
              <img 
                src={user ? userAvatar : '/assets/default-avatar.png'} 
                alt="Driver Profile" 
                className="w-full h-full object-cover group-hover:scale-110 transition-all duration-500"
              />
            </div>
            {isCollapsed ? (
              <span className="text-[8px] font-display font-black text-game-primary uppercase mt-2 truncate max-w-[60px] text-center">{user ? userName : 'VISITANTE'}</span>
            ) : (
              <h3 className="font-label text-xs tracking-widest uppercase text-console-text font-bold group-hover:text-game-primary transition-colors">{user ? userName : 'VISITANTE'}</h3>
            )}
          </div>
          {!isCollapsed && (
            <button onClick={onClose} className="md:hidden text-console-muted hover:text-console-text">
               <X size={20} />
            </button>
          )}
        </div>

        <nav className="flex-1 space-y-2 overflow-y-auto no-scrollbar">
          <NavItem 
            icon={<LayoutDashboard size={18} />} 
            label={t('console.overview') || 'Dashboard'} 
            active={location.pathname === '/console'}
            activeColor={selectedGame.color}
            onClick={() => handleNavigation('/console')}
            isCollapsed={isCollapsed}
          />
          <NavItem 
            icon={<CalendarDays size={18} />} 
            label={t('console.events')} 
            active={location.pathname === '/events'}
            activeColor={selectedGame.color}
            onClick={() => handleNavigation('/events')}
            isCollapsed={isCollapsed}
          />
          <NavItem 
            icon={<Users size={18} />} 
            label={t('teams.title')} 
            active={location.pathname.startsWith('/teams')}
            activeColor={selectedGame.color}
            onClick={() => handleNavigation('/teams')} 
            isCollapsed={isCollapsed}
          />
          <NavItem 
            icon={<Trophy size={18} />} 
            label={t('leagues.title')} 
            active={location.pathname.startsWith('/leagues')}
            activeColor={selectedGame.color}
            onClick={() => handleNavigation('/leagues')} 
            isCollapsed={isCollapsed}
          />
          <NavItem 
            icon={<TrendingUp size={18} />} 
            label={t('ranking.title')} 
            active={location.pathname === '/ranking'}
            activeColor={selectedGame.color}
            onClick={() => handleNavigation('/ranking')} 
            isCollapsed={isCollapsed}
          />
          <NavItem 
            icon={<History size={18} />} 
            label={t('console.results')} 
            active={location.pathname === '/history'}
            activeColor={selectedGame.color}
            onClick={() => handleNavigation('/history')}
            isCollapsed={isCollapsed}
          />
          {emssUser?.roles?.includes('admin') && (
            <NavItem 
              icon={<BarChart2 size={18} />} 
              label="Leaderboard" 
              active={location.pathname === '/leaderboard'}
              activeColor={selectedGame.color}
              onClick={() => handleNavigation('/leaderboard')}
              isCollapsed={isCollapsed}
            />
          )}
          {/* Marketplace — visível a qualquer usuário autenticado
              (a associação afiliado/parceiro é a porta de entrada para criar) */}
          <NavItem 
            icon={<ShoppingBag size={18} />} 
            label="Marketplace" 
            active={location.pathname.startsWith('/marketplace')}
            activeColor={selectedGame.color}
            onClick={() => handleNavigation('/marketplace')}
            isCollapsed={isCollapsed}
          />
          {emssUser?.roles?.includes('steward') && (
            <NavItem 
              icon={<Scale size={18} />} 
              label={t('console.stewardship')} 
              active={location.pathname === '/stewardship'}
              activeColor="#F9CA36"
              onClick={() => handleNavigation('/stewardship')} 
              isCollapsed={isCollapsed}
              badgeCount={pendingStewardshipCount}
            />
          )}
          {(emssUser?.roles?.includes('admin') || emssUser?.roles?.includes('broadcaster')) && (
            <NavItem 
              icon={<Mic2 size={18} />} 
              label="Transmissões"
              active={location.pathname === '/broadcasting'}
              activeColor="#7c3aed"
              onClick={() => handleNavigation('/broadcasting')} 
              isCollapsed={isCollapsed}
            />
          )}

          <NavItem 
            icon={<MessageSquare size={18} />} 
            label="Mensagens"
            active={location.pathname.startsWith('/messages')}
            activeColor={selectedGame.color}
            onClick={() => handleNavigation('/messages')}
            isCollapsed={isCollapsed}
            badgeCount={unreadMsgCount}
          />
          {emssUser?.roles?.includes('admin') && (
            <>
              <NavItem
                icon={<Bot size={18} />}
                label={t('agents.title')}
                active={location.pathname === '/agents'}
                activeColor={selectedGame.color}
                onClick={() => handleNavigation('/agents')}
                isCollapsed={isCollapsed}
              />
              <NavItem
                icon={<Gauge size={18} />}
                label={t('telemetry.title')}
                active={location.pathname === '/telemetry'}
                activeColor={selectedGame.color}
                onClick={() => handleNavigation('/telemetry')}
                isCollapsed={isCollapsed}
              />
            </>
          )}
          <NavItem
            icon={<Settings size={18} />}
            label={t('console.settings')}
            active={location.pathname === '/settings'}
            activeColor={selectedGame.color}
            onClick={() => handleNavigation('/settings')}
            isCollapsed={isCollapsed}
          />
          {emssUser?.roles?.includes('admin') && (
            <NavItem 
              icon={<ShieldAlert size={18} />} 
              label={t('stewardship.administration')} 
              active={location.pathname === '/manager'}
              activeColor="#FF3B30"
              onClick={() => handleNavigation('/manager')}
              isCollapsed={isCollapsed} 
            />
          )}
        </nav>



        <div className={`space-y-2 pt-6 border-t border-console-border ${isCollapsed ? 'flex flex-col items-center' : ''}`}>
          {!isCollapsed && (
            <div className="px-2 mb-6 transform scale-75 origin-left" id="sidebar-language-selector">
              <LanguageSelector />
            </div>
          )}
          
          <div id="sidebar-pwa-install">
            <InstallPrompt variant="sidebar" />
          </div>
          
          <a 
            href="mailto:emsseries@gmail.com?subject=Suporte: Chamado"
            title={t('console.support')}
            id="sidebar-support-link"
            className={`flex items-center gap-3 px-4 py-2 font-label text-xs text-console-muted hover:text-console-text transition-all duration-300 hover:scale-105 active:scale-95 w-full text-left ${isCollapsed ? 'justify-center px-0' : ''}`}
          >
            <HelpCircle size={14} />
            {!isCollapsed && <span>{t('console.support')}</span>}
          </a>
          
          {user ? (
            <button 
              onClick={handleLogout}
              title={t('console.logout')}
              id="sidebar-logout-button"
              className={`flex items-center gap-3 px-4 py-2 font-label text-xs text-console-muted hover:text-console-text transition-colors w-full text-left ${isCollapsed ? 'justify-center px-0' : ''}`}
            >
              <LogOut size={14} />
              {!isCollapsed && <span>{t('console.logout')}</span>}
            </button>
          ) : (
            <button 
              onClick={() => handleNavigation('/login')}
              title="Entrar / Cadastrar"
              className={`flex items-center gap-3 px-4 py-2 font-label text-xs text-game-primary font-bold hover:brightness-125 transition-colors w-full text-left ${isCollapsed ? 'justify-center px-0' : ''}`}
            >
              <LogOut size={14} className="rotate-180" />
              {!isCollapsed && <span>Entrar / Cadastrar</span>}
            </button>
          )}
        </div>
      </aside>
    </>
  );
};

/**
 * Chip de progresso do ciclo MRCI — ex.: "S06 • SEMANA 3/8" + 8 pontos.
 * Renderizado ao lado do seletor de jogo; some quando o jogo não tem ciclo ativo.
 */
const CycleProgressChip = ({ gameId, color }: { gameId?: string; color: string }) => {
  const { t } = useTranslation();
  const { cycle } = useActiveMrciCycle(gameId);

  if (!cycle) return null;

  return (
    <div
      className="hidden sm:flex items-center gap-2 px-2.5 sm:px-3 py-1 rounded-full neo-in bg-console-bg border shrink-0"
      style={{ borderColor: `${color}40` }}
      title={t('console.cycle.tooltip', {
        label: cycle.label,
        week: cycle.currentWeek,
        total: cycle.totalWeeks,
      })}
    >
      {/* Prefixo da season ativa */}
      <span
        className="text-[10px] font-black uppercase tracking-widest"
        style={{ color }}
      >
        {cycle.seasonPrefix}
      </span>

      <span className="w-px h-3 bg-white/10" />

      {/* Semana atual em relação às 8 do ciclo */}
      <span className="text-[9px] font-bold uppercase tracking-wider text-console-text whitespace-nowrap">
        {t('console.cycle.week')} {cycle.currentWeek}
        <span className="text-console-muted">/{cycle.totalWeeks}</span>
      </span>

      {/* 8 pontos de progresso — concluído / atual (pisca) / pendente */}
      <div className="hidden lg:flex items-center gap-[3px]" aria-hidden="true">
        {Array.from({ length: cycle.totalWeeks }).map((_, idx) => {
          const weekNumber = idx + 1;
          const isDone = weekNumber < cycle.currentWeek;
          const isCurrent = weekNumber === cycle.currentWeek;
          return (
            <span
              key={weekNumber}
              className={`h-1.5 w-1.5 rounded-full transition-all duration-300 ${isCurrent ? 'animate-pulse' : ''}`}
              style={{
                backgroundColor: isDone || isCurrent ? color : 'rgba(255,255,255,0.12)',
                boxShadow: isCurrent ? `0 0 6px ${color}` : undefined,
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

const TopBar = ({ onMenuClick, onToggleSidebar, isCollapsed }: { onMenuClick: () => void, onToggleSidebar: () => void, isCollapsed: boolean }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { selectedGame, setSelectedGame } = useGame();
  const { user, onlineCount } = useAuth();
  const [isGameMenuOpen, setIsGameMenuOpen] = useState(false);

  // Disable selector on Teams (/teams), Leagues (/leagues), Stewardship (/stewardship), and Settings (/settings)
  const isDisabled = 
    location.pathname.startsWith('/teams') || 
    location.pathname.startsWith('/leagues') || 
    location.pathname === '/stewardship' || 
    location.pathname === '/settings';

  useEffect(() => {
    if (isDisabled) {
      setIsGameMenuOpen(false);
    }
  }, [isDisabled]);

  return (
    <header 
      className={`fixed top-0 right-0 h-16 bg-console-bg/80 backdrop-blur-xl flex justify-between items-center px-3 sm:px-4 md:px-8 z-30 border-b border-console-border transition-all duration-300 shadow-sm left-0 ${
        isCollapsed ? 'md:left-[80px]' : 'md:left-[220px]'
      }`}
    >
      <div className="flex items-center gap-2 sm:gap-4 md:gap-6 min-w-0">
        <button onClick={onMenuClick} className="md:hidden text-console-text hover:text-console-muted p-1.5 shrink-0">
           <Menu size={20} />
        </button>
        
        <button 
          onClick={onToggleSidebar} 
          className="hidden md:flex items-center justify-center text-console-muted hover:text-console-text p-2 neo-out rounded-lg transition-all"
        >
          <Menu size={20} className={`transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`} />
        </button>

        <span className="text-xl font-black text-game-primary font-label hidden lg:block">CONSOLE</span>
        
        <button 
          onClick={() => navigate('/')}
          className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 rounded-lg text-[10px] font-bold text-console-muted hover:text-console-text hover:bg-white/5 transition-all uppercase tracking-widest border border-transparent hover:border-console-border shrink-0"
          title={t('console.back_to_site')}
        >
          <Home size={14} />
          <span className="hidden sm:block">{t('console.back_to_site')}</span>
        </button>
        
        {/* Contador de Usuários Online */}
        <div className="flex items-center gap-1.5 px-2 sm:px-3 py-1 bg-game-primary/10 rounded-full border border-game-primary/20 shrink-0" title={`${onlineCount} usuários online`}>
          <div className="w-1.5 h-1.5 rounded-full bg-game-primary animate-pulse"></div>
          <Users size={12} className="text-game-primary" />
          <span className="text-[10px] font-bold text-game-primary">{onlineCount}</span>
        </div>
        
        {/* Game Selector */}
        <div className="relative max-w-[120px] sm:max-w-[160px] min-w-0">
          <button 
            disabled={isDisabled}
            onClick={() => !isDisabled && setIsGameMenuOpen(!isGameMenuOpen)}
            title={isDisabled ? t('console.game_selector_disabled') || 'Seletor desabilitado nesta tela' : ''}
            className={`w-full neo-in px-2.5 sm:px-4 py-1.5 rounded-lg flex justify-between items-center text-[9px] sm:text-[10px] font-bold transition-all border ${
              isDisabled 
                ? 'bg-console-bg/30 border-console-border/30 text-console-muted cursor-not-allowed opacity-50' 
                : 'bg-console-bg border-console-border cursor-pointer hover:border-console-border/80'
            }`}
            style={isDisabled ? {} : { color: selectedGame.color }}
          >
            <span className="truncate pr-1">{selectedGame.name.toUpperCase()}</span> 
            <ChevronDown size={14} className={`min-w-[12px] transition-transform shrink-0 ${isGameMenuOpen ? 'rotate-180' : ''} ${isDisabled ? 'text-console-muted/30' : ''}`} />
          </button>

          {!isDisabled && isGameMenuOpen && (
            <>
              {/* Overlay invisível para fechar ao clicar fora */}
              <div className="fixed inset-0 z-40" onClick={() => setIsGameMenuOpen(false)} />
              
              <div className="absolute top-full left-0 w-48 mt-2 bg-console-surface rounded-lg shadow-2xl border border-console-border transition-all z-50 overflow-hidden">
                {GAMES.map((game) => (
                  <button 
                    key={game.id}
                    onClick={() => {
                      setSelectedGame(game);
                      setIsGameMenuOpen(false);
                    }}
                    className="w-full px-4 py-2.5 text-left text-[10px] font-label hover:bg-game-primary/5 transition-colors flex items-center justify-between text-console-text"
                  >
                    <span className="truncate pr-2">{game.name}</span>
                    <div className="w-2 h-2 rounded-full min-w-[8px]" style={{ backgroundColor: game.color }}></div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Progresso do ciclo MRCI (8 semanas) + prefixo da season ativa */}
        <CycleProgressChip gameId={selectedGame?.id} color={selectedGame?.color || '#98C136'} />
      </div>

      <div className="flex items-center gap-2 sm:gap-4 md:gap-6 shrink-0">
        {user ? (
          <div className="flex items-center gap-2 sm:gap-4">
            <NotificationDropdown />
            <WalletDropdown />
            <IconButton icon={<Flag size={18} />} className="hidden sm:block" />
          </div>
        ) : (
          <button
            onClick={() => navigate('/login', { state: { from: location } })}
            className="flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 bg-game-primary text-black font-black text-[11px] sm:text-xs uppercase tracking-widest rounded-xl hover:brightness-110 transition-all shadow-md shrink-0"
          >
            Entrar / Cadastrar
          </button>
        )}
      </div>
    </header>
  );
};

// ── Tier order for promotion detection ───────────────────────
const TIER_ORDER: Record<string, number> = { R: 0, D: 1, C: 2, B: 3, A: 4 };

const TIER_STORAGE_KEY = (userId: string) => `emss_last_tier_${userId}`;

const ConsoleLayout: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const defenseId = searchParams.get('defense');

  const { emssUser } = useAuth();
  const { selectedGame } = useGame();
  const { promotionData, clearPromotion } = useTierPromotion(emssUser, selectedGame?.id);
  const { grantData, clearGrant } = useMarblesGrant(emssUser);

  const handleCloseDefense = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('defense');
    setSearchParams(newParams, { replace: true });
  };

  return (
    <div className="min-h-screen bg-console-bg overflow-x-hidden flex transition-colors duration-500 max-w-full">
      <Sidebar 
        isOpen={isMobileMenuOpen} 
        onClose={() => setIsMobileMenuOpen(false)} 
        isCollapsed={isCollapsed}
      />
      <CountryCheckModal />
      <DefenseModal 
        isOpen={!!defenseId} 
        onClose={handleCloseDefense} 
        incidentId={defenseId || ''} 
      />
      <PrivacyConsentModal />
      {promotionData && (
        <PromotionModal
          isOpen
          newTier={promotionData.newTier}
          prevTier={promotionData.prevTier}
          pilotName={emssUser?.display_name || emssUser?.username || 'Piloto'}
          onClose={clearPromotion}
        />
      )}
      {grantData && (
        <MarblesGrantModal
          isOpen
          mode="received"
          amount={grantData.amount}
          onClose={clearGrant}
        />
      )}
      <div className="flex-1 flex flex-col min-h-screen min-w-0 max-w-full overflow-x-hidden">
        <TopBar 
          onMenuClick={() => setIsMobileMenuOpen(true)} 
          onToggleSidebar={() => setIsCollapsed(!isCollapsed)}
          isCollapsed={isCollapsed}
        />
        <main 
          className={`flex-1 pt-16 min-h-screen bg-console-bg transition-all duration-300 min-w-0 max-w-full overflow-x-hidden
            ${isCollapsed ? 'md:pl-[80px]' : 'md:pl-[220px]'} pl-0`}
        >
          <div className="max-w-[1600px] w-full p-3 sm:p-4 md:p-8 mx-auto min-w-0">
             <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default ConsoleLayout;

